/**
 * Utility to parse lesson markdown and custom tags into HTML.
 * Handles headers, bold, lists, and custom tags like [[interaction]] and [[video]].
 */

/**
 * Unicode-safe base64 encoding.
 * btoa() only supports ASCII, so we encode Unicode strings as UTF-8 first.
 */
export const toBase64 = (str: string): string => {
    try {
        return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_match, p1) => {
            return String.fromCharCode(parseInt(p1, 16));
        }));
    } catch (e) {
        console.error('toBase64 error:', e);
        return '';
    }
};

/**
 * Escape HTML special characters to prevent XSS.
 * Also handles '%' to prevent Vite "URI malformed" errors.
 */
const escapeAttribute = (text: string | null | undefined): string => {
    if (text == null) return '';
    return String(text)
        .replace(/&/g, '&amp;')
        .replace(/%/g, '&percnt;') // Encode % to prevent Vite decodeURI errors
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
};

/**
 * Remove surrounding quotes from a string, handling escaped quotes inside.
 */
const removeQuotes = (str: string): string => {
    str = str.trim();
    if ((str.startsWith('"') && str.endsWith('"')) ||
        (str.startsWith("'") && str.endsWith("'"))) {
        return str.slice(1, -1).replace(/\\"/g, '"').replace(/\\'/g, "'");
    }
    return str;
};

/**
 * Split a string by pipe character, respecting quoted strings.
 * Pipes inside quotes are not treated as delimiters.
 * Handles escaped quotes like \" inside quoted fields.
 */
const splitByPipeRespectingQuotes = (str: string): string[] => {
    const fields: string[] = [];
    let currentField = '';
    let inQuotes = false;

    for (let i = 0; i < str.length; i++) {
        const char = str[i];
        const prevChar = i > 0 ? str[i - 1] : '';

        if (char === '"' && prevChar !== '\\') {
            inQuotes = !inQuotes;
            currentField += char;
        } else if (char === '|' && !inQuotes) {
            fields.push(currentField.trim());
            currentField = '';
        } else {
            currentField += char;
        }
    }

    if (currentField.trim()) {
        fields.push(currentField.trim());
    }

    return fields;
};

/**
 * Safely encode a URL for use in src or href attributes.
 * Prevents "URI malformed" errors in Vite/Browser.
 * Automatically resolves local /assets/ paths to Firebase Storage.
 */
const safeUrl = (url: string | null | undefined): string => {
    if (!url) return '';
    let normalized = url.trim().replace(/\\/g, '/');

    // 0. If the URL is already a Firebase Storage URL or emulator URL, pass it through as-is
    //    This prevents double-encoding when content comes from Firestore (sync script already converted paths)
    if (
        normalized.includes('storage.googleapis.com/') ||
        normalized.includes('127.0.0.1:9199') ||
        normalized.includes('localhost:9199')
    ) {
        return normalized;
    }

    if (normalized.includes('firebasestorage.googleapis.com/')) {
        // Convert old REST API format to direct GCS format to avoid %2F encoding issues
        const match = normalized.match(/firebasestorage\.googleapis\.com\/v0\/b\/([^/]+)\/o\/(.+?)(\?|$)/);
        if (match) {
            const bucket = match[1];
            const encodedPath = match[2];
            // Decode the %2F back to / for the GCS format
            const decodedPath = decodeURIComponent(encodedPath);
            return `https://storage.googleapis.com/${bucket}/${decodedPath}`;
        }
        return normalized;
    }

    // 1. Handle Firebase Storage mapping for assets
    let cleanPath = normalized;
    if (cleanPath.startsWith('public/')) cleanPath = cleanPath.replace(/^public\//, '');
    if (cleanPath.startsWith('/')) cleanPath = cleanPath.substring(1);

    const BUCKET = 'fastrack-driving-school-lms.firebasestorage.app';
    let isFirebaseStorage = false;

    if (cleanPath.startsWith('assets/images/')) {
        cleanPath = cleanPath.replace('assets/images/', 'curriculum/images/');
        isFirebaseStorage = true;
    } else if (cleanPath.startsWith('assets/animations/')) {
        cleanPath = cleanPath.replace('assets/animations/', 'curriculum/animations/');
        isFirebaseStorage = true;
    } else if (cleanPath.startsWith('curriculum/images/') || cleanPath.startsWith('curriculum/animations/')) {
        isFirebaseStorage = true;
    }

    if (isFirebaseStorage && !normalized.startsWith('http')) {
        return `https://storage.googleapis.com/${BUCKET}/${cleanPath}`;
    }

    // 2. Normal encoding fallback
    normalized = normalized.replace(/%(?![0-9A-Fa-f]{2})/g, '%25');

    try {
        return encodeURI(normalized);
    } catch (e) {
        return normalized;
    }
};

/**
 * Parse inline markdown like bold, italic, and links.
 */
const parseInlines = (text: string): string => {
    if (!text) return '';
    let result = text;
    // 1. Links: [text](url)
    result = result.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    // 2. Bold
    result = result.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // 3. Italic
    result = result.replace(/\*(.*?)\*/g, '<em>$1</em>');
    return result;
};

export const parseLessonMarkdown = (markdown: string): string => {
    if (!markdown) return '';

    let html = markdown;

    // 1. Handle Headers
    html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');
    html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
    html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
    html = html.replace(/^#### (.*$)/gim, '<h4>$1</h4>');

    // Handle --- as a "Slide Transition" / Section Break
    html = html.replace(/^---$/gm, '<div class="lesson-animation-placeholder" data-description="slide transition"></div>');

    // 2. Handle Lists
    html = html.replace(/(?:^\* .*\n?)+/gm, (match) => {
        const items = match.trim().split('\n').map(item => `<li>${parseInlines(item.substring(2))}</li>`).join('');
        return `<ul>${items}</ul>`;
    });

    html = html.replace(/(?:^\d+\. .*\n?)+/gm, (match) => {
        const items = match.trim().split('\n').map(item => `<li>${parseInlines(item.replace(/^\d+\. /, ''))}</li>`).join('');
        return `<ol>${items}</ol>`;
    });

    // 2.5 Handle GitHub-style Alerts: > [!IMPORTANT]
    html = html.replace(/^[ \t]*>[ \t]*\[!IMPORTANT\][ \t]*\r?\n?((?:^[ \t]*>.*\r?\n?)*)/gim, (_match, content) => {
        const cleanContent = content.replace(/^[ \t]*>[ \t]?/gm, '').trim();
        return `<div class="lesson-callout-placeholder" data-type="important" data-title="IMPORTANT" data-content="${escapeAttribute(parseInlines(cleanContent))}"></div>`;
    });

    // Handle GitHub-style Alerts: > [!OPTIONAL]
    html = html.replace(/^[ \t]*>[ \t]*\[!OPTIONAL\][ \t]*\r?\n?((?:^[ \t]*>.*\r?\n?)*)/gim, (_match, content) => {
        const cleanContent = content.replace(/^[ \t]*>[ \t]?/gm, '').trim();
        return `<div class="lesson-callout-placeholder" data-type="optional" data-title="OPTIONAL" data-content="${escapeAttribute(parseInlines(cleanContent))}"></div>`;
    });

    // 3. Handle Custom Tags (PROCESS LEAF NODES FIRST FOR NESTING SUPPORT)
    // NOTE: Bold & Italic conversion is moved to AFTER custom tag parsing
    // so that markdown syntax like **text** is preserved in custom tag data

    // A. Leaf Nodes (Terms, Media, Animations)
    html = html.replace(/\[\[term:\s*([^|\]]*?)\s*\|\s*([\s\S]*?)\s*\]\]/gi, (_match, label, definition) => {
        const cleanLabel = (label || '').trim().replace(/^["'](.*)["']$/, '$1');
        const cleanDef = (definition || '').trim().replace(/^["'](.*)["']$/, '$1');
        return `<span class="lesson-term-placeholder" data-label="${escapeAttribute(parseInlines(cleanLabel))}" data-definition="${escapeAttribute(parseInlines(cleanDef))}"></span>`;
    });

    html = html.replace(/\[\[video:\s*([\s\S]*?)\s*\]\]/gi, (match, content) => {
        const parts = content.split('|').map(p => p.trim());
        if (!parts.length || !parts[0]) return match;

        // Try to find the URL. If it's a multi-part tag (like Lesson 1-4), the URL is usually last.
        // If it's a 2-part tag (URL | optional), it depends.
        let rawUrl = parts[0];
        let optional = parts.includes('optional');
        const nonOptionalParts = parts.filter(p => p !== 'optional');
        
        if (nonOptionalParts.length >= 3) {
            // Likely format: [[video: type | duration | url]]
            rawUrl = nonOptionalParts[nonOptionalParts.length - 1];
        } else if (nonOptionalParts.length === 2) {
            // Format: [[video: url | optional]] or [[video: type | url]]
            rawUrl = nonOptionalParts[nonOptionalParts.length - 1];
        } else if (nonOptionalParts.length === 1) {
            rawUrl = nonOptionalParts[0];
        }

        const cleanUrl = rawUrl.replace(/^["'](.*)["']$/, '$1');
        const isRequired = !optional;
        const normalizedUrl = safeUrl(cleanUrl);
        const isYouTube = cleanUrl.includes('youtube.com') || cleanUrl.includes('youtu.be');

        if (isYouTube) {
            let videoId = '';
            const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([A-Za-z0-9_-]{11})/i;
            const matchResult = cleanUrl.match(youtubeRegex);
            if (matchResult && matchResult[1]) videoId = matchResult[1];

            if (videoId) {
                const embedUrl = `https://www.youtube.com/embed/${videoId}`;
                return `
                    <div class="inline-video-card youtube-video-card" data-video-url="${escapeAttribute(embedUrl)}" data-required="${isRequired}">
                        <div class="video-card-thumbnail youtube-embed-thumbnail">
                            <img src="https://img.youtube.com/vi/${videoId}/mqdefault.jpg" alt="Video thumbnail" />
                            <div class="video-card-icon youtube-icon">▶</div>
                        </div>
                        <div class="video-card-content">
                            <div class="video-card-title">${isRequired ? 'Required Supplemental Video' : 'Supplemental Video'}</div>
                            <div class="video-card-action">Click to watch in theatre mode</div>
                        </div>
                    </div>
                `;
            }
        }

        return `
            <div class="inline-video-card" data-video-url="${escapeAttribute(normalizedUrl)}" data-required="${isRequired}">
                <div class="video-card-thumbnail">
                    <video src="${normalizedUrl}" muted playsinline preload="metadata"></video>
                    <div class="video-card-icon">▶</div>
                </div>
                <div class="video-card-content">
                    <div class="video-card-title">${isRequired ? 'Required Supplemental Video' : 'Supplemental Video'}</div>
                    <div class="video-card-action">Click to watch in theatre mode</div>
                </div>
            </div>
        `;
    });

    html = html.replace(/\[\[image:\s*([^|\]]*?)(?:\s*\|\s*([^|\]]*?))?(?:\s*\|\s*([^|\]]*?))?(?:\s*\|\s*([^|\]]*?))?\s*\]\]/gi, (_match, url, alt, caption, type) => {
        let cleanUrl = (url || '').trim();
        if (cleanUrl.startsWith('public/')) cleanUrl = cleanUrl.replace(/^public\//, '/');
        if (cleanUrl && !cleanUrl.startsWith('/') && !cleanUrl.startsWith('http')) cleanUrl = `/${cleanUrl}`;

        const normalizedUrl = safeUrl(cleanUrl);
        const cleanAlt = (alt || '').trim().replace(/^["'](.*)["']$/, '$1');
        const cleanCaption = (caption || '').trim().replace(/^["'](.*)["']$/, '$1');
        const cleanType = (type || '').trim().replace(/^["'](.*)["']$/, '$1') || 'bar';

        return `
            <div class="lesson-image-container" data-caption="${escapeAttribute(parseInlines(cleanCaption))}" data-caption-type="${escapeAttribute(cleanType)}">
                <img src="${normalizedUrl}" alt="${escapeAttribute(cleanAlt || 'Lesson Image')}" class="lesson-image" loading="lazy" />
            </div>
        `;
    });

    html = html.replace(/\[\[infographic:\s*([^|\]]*?)\]\]/gi, (_match, type) => `<div class="lesson-infographic-placeholder" data-type="${escapeAttribute(type.trim())}"></div>`);
    html = html.replace(/\[\[animation:\s*([^|\]]*?)\]\]/gi, (_match, desc) => `<div class="lesson-animation-placeholder" data-description="${escapeAttribute(desc.trim())}"></div>`);
    html = html.replace(/\[\[lottie:\s*([^|\]]*?)(?:\s*\|\s*([^|\]]*?))?\]\]/gi, (_match, url, desc) => {
        const cleanUrl = (url || '').trim().replace(/^["'](.*)["']$/, '$1');
        if (!cleanUrl) return ''; // Skip if no URL
        const cleanDesc = (desc || '').trim().replace(/^["'](.*)["']$/, '$1');
        return `<div class="lesson-lottie-placeholder" data-url="${safeUrl(cleanUrl)}" data-description="${escapeAttribute(parseInlines(cleanDesc) || 'Animation')}"></div>`;
    });

    html = html.replace(/\[\[quiz-trigger:\s*([^\]]*?)\s*\]\]/gi, (_match, quizId) => {
        const cleanQuizId = quizId.trim().replace(/^["'](.*)["']$/, '$1');
        return `<div class="lesson-quiz-trigger-placeholder" data-quiz-id="${escapeAttribute(cleanQuizId)}"></div>`;
    });

    html = html.replace(/\[\[review-trigger:\s*([^\]]*?)\s*\]\]/gi, (_match, quizId) => {
        const cleanQuizId = quizId.trim().replace(/^["'](.*)["']$/, '$1');
        return `<div class="lesson-review-trigger-placeholder" data-quiz-id="${escapeAttribute(cleanQuizId)}"></div>`;
    });

    html = html.replace(/\[\[pvc:\s*([\s\S]*?)\s*\]\]/gi, (match, content) => {
        const parts = content.split('|').map(p => p.trim());
        if (parts.length < 4) return match; // Not a full PVC tag

        const id = parts[0];
        const question = parts[1].replace(/^["'](.*)["']$/, '$1');
        const optionsRaw = parts[2];
        const correct = parts[3].replace(/^["'](.*)["']$/, '$1');

        const cleanId = id.trim();
        const cleanQuestion = parseInlines(question.trim());
        
        let optionsArray: string[] = [];
        try {
            if (optionsRaw.startsWith('[') && optionsRaw.endsWith(']')) {
                optionsArray = JSON.parse(optionsRaw);
            } else {
                throw new Error('Not JSON');
            }
        } catch {
            optionsArray = optionsRaw.split(';').map(o => o.trim().replace(/^["'](.*)["']$/, '$1'));
        }
        
        const optionsJson = JSON.stringify(optionsArray.map(o => parseInlines(o)));
        const cleanCorrect = correct.trim();
        
        return `<div class="lesson-pvc-placeholder" data-pvc-id="${escapeAttribute(cleanId)}" data-question="${escapeAttribute(cleanQuestion)}" data-options="${escapeAttribute(optionsJson)}" data-correct="${escapeAttribute(cleanCorrect)}"></div>`;
    });

    // B. Intermediate Containers (Can contain Leaf Nodes)
    html = html.replace(/\[\[interaction:\s*([\w-]+)\s*\|\s*([\s\S]*?)\s*\]\]/gi, (match, type, content) => {
        const trimmedType = type.trim();
        const trimmedContent = content.trim();

        if (trimmedType === 'next-lesson-preview' || trimmedType === 'coming-up-next') {
            return `<div class="lesson-next-preview-placeholder"></div>`;
        }

        if (trimmedContent.startsWith('{') && trimmedContent.endsWith('}')) {
            try {
                const data = JSON.parse(trimmedContent);
                const safeJson = JSON.stringify(data);
                const escapedType = escapeAttribute(trimmedType);
                const label = data.trigger || data.title || trimmedType;

                return `<div class="lesson-interaction-placeholder" data-type="${escapedType}" data-label="${escapeAttribute(parseInlines(label))}" data-content="${escapeAttribute(safeJson)}"></div>`;
            } catch (e) {
                // If JSON fails, fall through to parts-based parsing
            }
        }

        const parts = trimmedContent.split('|').map(p => p.trim().replace(/^["'](.*)["']$/, '$1'));
        if (trimmedType === 'scenario-select' || trimmedType === 'pop-quiz') {
            let question = '';
            let optionsRaw = '';
            let correct = '';

            if (parts.length >= 3) {
                question = parts[0];
                optionsRaw = parts[1];
                correct = parts[2];
            } else if (trimmedType === 'scenario-select') {
                // Try to parse keyed arguments: options="...", answer="..."
                parts.forEach(p => {
                    if (p.includes('=')) {
                        const [key, value] = p.split('=').map(s => s.trim().replace(/^["'](.*)["']$/, '$1'));
                        if (key === 'options') optionsRaw = value;
                        else if (key === 'answer' || key === 'correct') correct = value;
                        else if (key === 'question') question = value;
                    }
                });
            }

            if (optionsRaw && correct) {
                const escapedQuestion = escapeAttribute(parseInlines(question));
                
                let optionsArray: string[] = [];
                try {
                    optionsArray = JSON.parse(optionsRaw);
                } catch {
                    optionsArray = (optionsRaw.includes(';') ? optionsRaw.split(';') : optionsRaw.split(',')).map(o => o.trim());
                }
                
                // Parse inlines in options to support bold/italics
                optionsArray = optionsArray.map(o => parseInlines(o));
                const optionsJson = JSON.stringify(optionsArray);
                
                const escapedCorrect = escapeAttribute(correct);
                const className = trimmedType === 'pop-quiz' 
                    ? 'lesson-inline-quiz-placeholder' 
                    : 'lesson-scenario-select-placeholder';

                return `<div class="${className}" data-question="${escapedQuestion}" data-options="${escapeAttribute(optionsJson)}" data-correct="${escapedCorrect}"></div>`;
            }
        }

        if (trimmedType === 'exam-reveal' && parts.length === 1) {
            const questionId = escapeAttribute(parts[0]);
            return `<div class="lesson-interaction-placeholder" data-type="exam-reveal" data-question-id="${questionId}"></div>`;
        }

        if (parts.length >= 2) {
            const label = escapeAttribute(parseInlines(parts[0]));
            const innerContent = parseInlines(parts[1]); // Parse inlines here
            const escapedType = escapeAttribute(trimmedType);
            
            // Check if there is a 3rd parameter (Question ID) for exam-reveal
            let questionIdAttr = '';
            if (trimmedType === 'exam-reveal' && parts.length >= 3) {
                const qId = escapeAttribute(parts[2]);
                questionIdAttr = ` data-question-id="${qId}"`;
            }
            
            return `<div class="lesson-interaction-placeholder" data-type="${escapedType}" data-label="${label}" data-content="${escapeAttribute(innerContent)}"${questionIdAttr}></div>`;
        }

        return match;
    });

    html = html.replace(/\[\[callout:\s*([\w-]+)\s*\|\s*([^|\]]*?)\s*\|\s*([\s\S]*?)\s*\]\]/gi, (_match, type, title, content) => {
        const cleanTitle = (title || '').trim().replace(/^["'](.*)["']$/, '$1');
        const cleanContent = (content || '').trim().replace(/^["'](.*)["']$/, '$1');
        return `<div class="lesson-callout-placeholder" data-type="${escapeAttribute(type)}" data-title="${escapeAttribute(parseInlines(cleanTitle))}" data-content="${escapeAttribute(parseInlines(cleanContent))}"></div>`;
    });

    // C. Structural Containers (Can contain Interactions/Callouts/Leafs)
    const parseCard = (variant: string, fields: any[]) => {
        const number = variant === 'default' ? fields[0] : '';
        const offset = variant === 'default' ? 1 : 0;

        const title = (fields[offset] || '').trim().replace(/^["'](.*)["']$/, '$1');
        const summary = (fields[offset + 1] || '').trim().replace(/^["'](.*)["']$/, '$1');
        const icon = (fields[offset + 2] || '').trim().replace(/^["'](.*)["']$/, '$1');
        let image = (fields[offset + 3] || '').trim().replace(/^["'](.*)["']$/, '$1');
        let caption = (fields[offset + 4] || '').trim().replace(/^["'](.*)["']$/, '$1');
        let fit = (fields[offset + 5] || '').trim().replace(/^["'](.*)["']$/, '$1') || 'cover';
        const lastField = (fields[offset + 6] || '').trim().replace(/^["'](.*)["']$/, '$1');

        let imageAlt = '';
        let lottieUrl = '';

        const isLottie = (url: string) => url.toLowerCase().includes('.lottie') || url.toLowerCase().includes('.json');

        if (isLottie(lastField)) {
            lottieUrl = lastField;
        } else if (isLottie(fit)) {
            lottieUrl = fit;
            fit = 'cover'; // Reset fit to default since fit was the lottie
        } else if (isLottie(caption)) {
            lottieUrl = caption;
            caption = ''; // Clear caption since caption was the lottie
        } else if (isLottie(image)) {
            lottieUrl = image;
            image = ''; // Clear image since image was the lottie
        }

        if (lastField && !isLottie(lastField)) {
            imageAlt = lastField;
        }

        const isImagePath = (url: string) => {
            if (!url) return false;
            const lower = url.toLowerCase();
            return lower.includes('/') || 
                   lower.endsWith('.png') || 
                   lower.endsWith('.jpg') || 
                   lower.endsWith('.jpeg') || 
                   lower.endsWith('.svg') || 
                   lower.endsWith('.webp') || 
                   lower.endsWith('.gif');
        };

        if (image && !isImagePath(image)) {
            image = '';
        }

        let cleanImage = image;
        if (cleanImage.startsWith('public/')) cleanImage = cleanImage.replace(/^public\//, '/');
        if (cleanImage && !cleanImage.startsWith('/') && !cleanImage.startsWith('http')) cleanImage = `/${cleanImage}`;

        let cleanLottie = lottieUrl;
        if (cleanLottie.startsWith('public/')) cleanLottie = cleanLottie.replace(/^public\//, '/');
        if (cleanLottie && !cleanLottie.startsWith('/') && !cleanLottie.startsWith('http')) cleanLottie = `/${cleanLottie}`;

        return `<div class="lesson-section-card-placeholder" data-variant="${variant}" data-number="${escapeAttribute(number)}" data-title="${escapeAttribute(parseInlines(title))}" data-summary="${escapeAttribute(parseInlines(summary))}" data-icon="${escapeAttribute(icon)}" data-image="${safeUrl(cleanImage)}" data-image-alt="${escapeAttribute(imageAlt)}" data-fit="${escapeAttribute(fit)}" data-caption="${escapeAttribute(parseInlines(caption))}" data-lottie-url="${safeUrl(cleanLottie)}"></div>`;
    };

    // Dedicated parser for stats-card which has a unique field structure:
    // 5-field format: title | statValue | description | icon | color
    // 7-field format: title | description | icon | "" | "" | "cover" | lottieUrl (legacy)
    const parseStatsCard = (variant: string, fields: any[]) => {
        const clean = (i: number) => (fields[i] || '').trim().replace(/^["'](.*?)["']$/, '$1');
        const fieldCount = fields.length;

        let title = clean(0);
        let summary = '';
        let icon = '';
        let lottieUrl = '';
        let image = '';
        let fit = 'cover';

        const isLottie = (url: string) => url.toLowerCase().includes('.lottie') || url.toLowerCase().includes('.json');

        if (fieldCount <= 5) {
            // Standard 5-field: title | statValue | description | icon | color
            // Combine statValue + description into summary for display
            const statValue = clean(1);
            const description = clean(2);
            summary = statValue ? `**${statValue}**<br/>${description}` : description;
            icon = clean(3);
            // fields[4] is color, not used as a prop but could be stored
        } else {
            // 7-field legacy: title | description | icon | "" | "" | "cover" | lottieUrl
            summary = clean(1);
            icon = clean(2);
            image = clean(3);
            fit = clean(5) || 'cover';
            const lastField = clean(6);
            if (lastField && isLottie(lastField)) {
                lottieUrl = lastField;
            }
            if (image && isLottie(image)) {
                lottieUrl = image;
                image = '';
            }
        }

        let cleanImage = image;
        if (cleanImage.startsWith('public/')) cleanImage = cleanImage.replace(/^public\//, '/');
        if (cleanImage && !cleanImage.startsWith('/') && !cleanImage.startsWith('http')) cleanImage = `/${cleanImage}`;

        let cleanLottie = lottieUrl;
        if (cleanLottie.startsWith('public/')) cleanLottie = cleanLottie.replace(/^public\//, '/');
        if (cleanLottie && !cleanLottie.startsWith('/') && !cleanLottie.startsWith('http')) cleanLottie = `/${cleanLottie}`;

        return `<div class="lesson-section-card-placeholder" data-variant="${variant}" data-number="" data-title="${escapeAttribute(parseInlines(title))}" data-summary="${escapeAttribute(parseInlines(summary))}" data-icon="${escapeAttribute(icon)}" data-image="${safeUrl(cleanImage)}" data-image-alt="" data-fit="${escapeAttribute(fit)}" data-caption="" data-lottie-url="${safeUrl(cleanLottie)}"></div>`;
    };

    html = html.replace(/\[\[section-card:\s*([^\]]*?)\]\]/gi, (_match, content) => parseCard('default', content.split('|').map(s => s.trim())));
    html = html.replace(/\[\[theme-card:\s*([^\]]*?)\]\]/gi, (_match, content) => parseCard('theme', content.split('|').map(s => s.trim())));
    html = html.replace(/\[\[authority-card:\s*([^\]]*?)\]\]/gi, (_match, content) => parseCard('authority', content.split('|').map(s => s.trim())));
    html = html.replace(/\[\[impact-card:\s*([^\]]*?)\]\]/gi, (_match, content) => parseCard('impact', content.split('|').map(s => s.trim())));
    html = html.replace(/\[\[stats-card:\s*([^\]]*?)\]\]/gi, (_match, content) => parseStatsCard('stats', content.split('|').map(s => s.trim())));

    const parseStepCard = (fields: any[]) => {
        const number = (fields[0] || '').trim().replace(/^["'](.*)["']$/, '$1');
        const title = (fields[1] || '').trim().replace(/^["'](.*)["']$/, '$1');
        const description = (fields[2] || '').trim().replace(/^["'](.*)["']$/, '$1');
        const icon = (fields[3] || '').trim().replace(/^["'](.*)["']$/, '$1');
        const image = (fields[4] || '').trim().replace(/^["'](.*)["']$/, '$1');

        let cleanImage = image;
        if (cleanImage.startsWith('public/')) cleanImage = cleanImage.replace(/^public\//, '/');
        if (cleanImage && !cleanImage.startsWith('/') && !cleanImage.startsWith('http')) cleanImage = `/${cleanImage}`;

        return `<div class="lesson-step-card-placeholder" data-number="${escapeAttribute(number)}" data-title="${escapeAttribute(parseInlines(title))}" data-description="${escapeAttribute(parseInlines(description))}" data-icon="${escapeAttribute(icon)}" data-image="${safeUrl(cleanImage)}"></div>`;
    };

    html = html.replace(/\[\[step-card:\s*([^\]]*?)\]\]/gi, (_match, content) => parseStepCard(content.split('|').map(s => s.trim())));

    // Parse Carousel: [[carousel: image1 | caption1 | image2 | caption2... ]]
    const parseCarousel = (fields: any[]) => {
        const items: { url: string; caption: string }[] = [];
        for (let i = 0; i < fields.length; i += 2) {
            const image = removeQuotes(fields[i] || '');
            const caption = removeQuotes(fields[i + 1] || '');
            if (image) {
                let cleanImage = image;
                if (cleanImage.startsWith('public/')) cleanImage = cleanImage.replace(/^public\//, '/');
                if (cleanImage && !cleanImage.startsWith('/') && !cleanImage.startsWith('http')) cleanImage = `/${cleanImage}`;
                
                items.push({
                    url: safeUrl(cleanImage),
                    caption: parseInlines(caption)
                });
            }
        }
        const itemsB64 = toBase64(JSON.stringify(items));
        return `<div class="lesson-carousel-placeholder" data-items-b64="${itemsB64}"></div>`;
    };

    html = html.replace(/\[\[carousel:\s*([^\]]*?)\]\]/gi, (_match, content) => parseCarousel(splitByPipeRespectingQuotes(content)));

    // Parse Video Card: [[video-card: number | title | summary | videoUrl | videoPoster | caption | variant]]
    const parseVideoCard = (fields: any[]) => {
        const number = (fields[0] || '').trim().replace(/^["'](.*)["']$/, '$1');
        const title = (fields[1] || '').trim().replace(/^["'](.*)["']$/, '$1');
        const summary = (fields[2] || '').trim().replace(/^["'](.*)["']$/, '$1');
        const videoUrl = (fields[3] || '').trim().replace(/^["'](.*)["']$/, '$1');
        const videoPoster = (fields[4] || '').trim().replace(/^["'](.*)["']$/, '$1');
        const caption = (fields[5] || '').trim().replace(/^["'](.*)["']$/, '$1');
        const variant = (fields[6] || 'default').trim().replace(/^["'](.*)["']$/, '$1');

        let cleanVideoUrl = videoUrl;
        if (cleanVideoUrl.startsWith('public/')) cleanVideoUrl = cleanVideoUrl.replace(/^public\//, '/');
        if (cleanVideoUrl && !cleanVideoUrl.startsWith('/') && !cleanVideoUrl.startsWith('http')) cleanVideoUrl = `/${cleanVideoUrl}`;

        let cleanPoster = videoPoster;
        if (cleanPoster.startsWith('public/')) cleanPoster = cleanPoster.replace(/^public\//, '/');
        if (cleanPoster && !cleanPoster.startsWith('/') && !cleanPoster.startsWith('http')) cleanPoster = `/${cleanPoster}`;

        return `<div class="lesson-video-card-placeholder" data-number="${escapeAttribute(number)}" data-title="${escapeAttribute(parseInlines(title))}" data-summary="${escapeAttribute(parseInlines(summary))}" data-video-url="${safeUrl(cleanVideoUrl)}" data-video-poster="${safeUrl(cleanPoster)}" data-caption="${escapeAttribute(parseInlines(caption))}" data-variant="${escapeAttribute(variant)}"></div>`;
    };

    html = html.replace(/\[\[video-card:\s*([^\]]*?)\]\]/gi, (_match, content) => parseVideoCard(content.split('|').map(s => s.trim())));

    // Parse Data Table: [[data-table: title | description | headers | row1 | row2... ]]
    const parseDataTable = (fields: any[]) => {
        const title = removeQuotes(fields[0] || '') || '';
        const description = removeQuotes(fields[1] || '') || '';
        const headersRaw = removeQuotes(fields[2] || '');
        const headers = headersRaw.split('|').map(h => h.trim()).filter(h => h).map(h => parseInlines(h));

        const rows = fields.slice(3).map(row => {
            const rowStr = removeQuotes(row || '');
            return rowStr.split('|').map(cell => cell.trim()).filter(cell => cell).map(c => parseInlines(c));
        }).filter(row => row.length > 0);

        // Use base64 encoding to avoid HTML entity encoding issues with JSON
        // Unicode-safe encoding to handle emojis/special chars
        const headersB64 = toBase64(JSON.stringify(headers));
        const rowsB64 = toBase64(JSON.stringify(rows));

        return `<div class="lesson-data-table-placeholder" data-title="${escapeAttribute(parseInlines(title))}" data-description="${escapeAttribute(parseInlines(description))}" data-headers-b64="${headersB64}" data-rows-b64="${rowsB64}"></div>`;
    };

    html = html.replace(/\[\[data-table:\s*([^\]]*?)\]\]/gi, (_match, content) => parseDataTable(splitByPipeRespectingQuotes(content)));

    html = html.replace(/\[\[hero:\s*([^|\]]*?)\s*\|\s*([^|\]]*?)\s*(?:\|\s*([^|\]]*?))?\]\]/gi, (_match, title, subtitle) => {
        const cleanTitle = (title || '').trim().replace(/^["'](.*)["']$/, '$1');
        const cleanSubtitle = (subtitle || '').trim().replace(/^["'](.*)["']$/, '$1');
        return `<div class="lesson-hero-placeholder" data-title="${escapeAttribute(parseInlines(cleanTitle))}" data-subtitle="${escapeAttribute(parseInlines(cleanSubtitle))}"></div>`;
    });

    // D. Final Assets
    const firstHeaderMatch = markdown.match(/^#+\s*(.*)$/m);
    const lessonTitle = firstHeaderMatch ? firstHeaderMatch[1].trim() : 'Lesson Narration';
    html = html.replace(/\[\[audio:\s*([^|\]]*?)\s*\|\s*([^\]]*?)\]\]/gi, (_match, url, duration) => {
        let cleanUrl = (url || '').trim().replace(/^["'](.*)["']$/, '$1');
        if (cleanUrl.startsWith('public/')) cleanUrl = cleanUrl.replace(/^public\//, '/');
        if (cleanUrl && !cleanUrl.startsWith('/') && !cleanUrl.startsWith('http')) cleanUrl = `/${cleanUrl}`;
        const cleanDuration = (duration || '').trim().replace(/^["'](.*)["']$/, '$1');
        return `<div class="lesson-audio-placeholder" data-url="${safeUrl(cleanUrl)}" data-duration="${escapeAttribute(cleanDuration)}" data-context="${escapeAttribute(lessonTitle)}"></div>`;
    });

    // 4. Handle Bold & Italic & Links (AFTER custom tags - fallback for non-custom tag content)
    html = parseInlines(html);

    // 5. Handle Line Breaks (AFTER tags)
    html = html.replace(/\n(?!(?:<\/?[a-z]))/gi, '<br />');

    // 6. Final Cleanups
    html = html.replace(/```(.*?)\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>');

    return html;
};
